import { forwardRef, useEffect, useMemo, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as SCThemeProvider } from 'styled-components';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Slide from '@mui/material/Slide';
import Snackbar from '@mui/material/Snackbar';
import { selectIsAuthenticated, selectUser, setAuth } from './store/slices/authSlice';
import { registerGlobalToastHandler } from './services/toastBus';
import ProtectedRoute from './components/ProtectedRoute';
import UnauthOnlyRoute from './components/UnauthOnlyRoute';
import Main from './pages/Main';
import SignIn from './pages/SignIn';
import ForgotPassword from './pages/ForgotPassword';
import DataDeleteScreen from './pages/DataDeleteScreen';
import JoinReferralScreen from './pages/JoinReferralScreen';
import { createAppTheme, THEME_MODES } from './theme';
import { ThemeModeProvider } from './context/ThemeModeContext';
import api from './services/api';
import { getAccessToken } from './services/authStorage';
import { PATH, ROUTE_PATTERN } from './constants/routes';

const THEME_STORAGE_KEY = 'drivio-theme-mode';

const ToastSlideTransition = forwardRef(function ToastSlideTransition(props, ref) {
  return <Slide {...props} ref={ref} direction="left" />;
});

const getInitialThemeMode = () => {
  if (typeof window === 'undefined') return THEME_MODES.DARK;

  const savedMode = localStorage.getItem(THEME_STORAGE_KEY);
  if (savedMode === THEME_MODES.DARK || savedMode === THEME_MODES.LIGHT) {
    return savedMode;
  }

  return window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ? THEME_MODES.DARK
    : THEME_MODES.LIGHT;
};

const App = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const authUser = useSelector(selectUser);
  const [themeMode, setThemeMode] = useState(getInitialThemeMode);
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'error',
    key: 0,
  });

  useEffect(() => {
    const unregister = registerGlobalToastHandler(({ message, severity }) => {
      setToast({
        open: true,
        message,
        severity: severity || 'error',
        key: Date.now(),
      });
    });

    return unregister;
  }, []);

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, themeMode);
    document.documentElement.style.colorScheme = themeMode;
  }, [themeMode]);

  useEffect(() => {
    const token = getAccessToken();

    if (!token || authUser) return;

    const fetchLoggedInUser = async () => {
      const response = await api.get('/auth/me', { skipErrorToast: true });

      if (!response?.ok) return;

      const user = response.data?.data;

      if (user) dispatch(setAuth(user));
    };

    fetchLoggedInUser();
  }, [authUser, dispatch]);

  const theme = useMemo(() => createAppTheme(themeMode), [themeMode]);

  const toggleThemeMode = () => {
    setThemeMode((prevMode) =>
      prevMode === THEME_MODES.DARK ? THEME_MODES.LIGHT : THEME_MODES.DARK
    );
  };

  const themeModeContextValue = useMemo(() => ({ themeMode, toggleThemeMode }), [themeMode]);

  const handleToastClose = (_, reason) => {
    if (reason === 'clickaway') return;

    setToast((prev) => ({ ...prev, open: false }));
  };

  return (
    <ThemeModeProvider value={themeModeContextValue}>
      <ThemeProvider theme={theme}>
        <SCThemeProvider theme={theme}>
          <CssBaseline />
          <Box
            sx={{
              height: '100vh',
              position: 'relative',
              minHeight: '100vh',
              boxSizing: 'border-box',
            }}
          >
            <Routes>
              <Route
                path={ROUTE_PATTERN.SIGN_IN}
                element={
                  <UnauthOnlyRoute>
                    <SignIn />
                  </UnauthOnlyRoute>
                }
              />

              <Route
                path={ROUTE_PATTERN.FORGOT_PASSWORD}
                element={
                  <UnauthOnlyRoute>
                    <ForgotPassword />
                  </UnauthOnlyRoute>
                }
              />

              <Route path={ROUTE_PATTERN.DATA_DELETE} element={<DataDeleteScreen />} />

              <Route path={ROUTE_PATTERN.JOIN} element={<JoinReferralScreen />} />

              <Route
                path={ROUTE_PATTERN.MAIN_WILDCARD}
                element={
                  <ProtectedRoute>
                    <Main />
                  </ProtectedRoute>
                }
              />

              <Route
                path="*"
                element={
                  isAuthenticated ? (
                    <Navigate to={PATH.HOME} replace />
                  ) : (
                    <Navigate to={PATH.SIGN_IN} replace />
                  )
                }
              />
            </Routes>
          </Box>

          {toast.open && (
            <Snackbar
              key={toast.key}
              open
              autoHideDuration={3000}
              onClose={handleToastClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              TransitionComponent={ToastSlideTransition}
            >
              <Alert
                onClose={handleToastClose}
                severity={toast.severity}
                color={toast.severity}
                variant="filled"
                sx={{ width: '100%' }}
                slotProps={{
                  action: {
                    sx: {
                      marginTop: 0,
                      paddingTop: 0,
                      alignItems: 'center',
                    },
                  },
                }}
              >
                {typeof toast.message === 'string'
                  ? toast.message
                  : (toast.message?.message ?? toast.message?.code ?? 'Une erreur est survenue.')}
              </Alert>
            </Snackbar>
          )}
        </SCThemeProvider>
      </ThemeProvider>
    </ThemeModeProvider>
  );
};

export default App;
